import { faArrowsCross, faArrowsSplitUpAndLeft } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge, Box, Tooltip } from '@mantine/core';
import { TextWithEmoji } from '@vision/ui/components';
import { SurveyLogicOperator } from '@vision/ui/interfaces/survey-logic-builder';
import { forwardRef } from 'react';
import classes from './ConditionValueToken.module.css';

type ConditionValueBadgeProps = {
  value: string;
  tooltipLabel?: string;
  tooltipOpenDelay?: number;
  onClick?: () => void;
  truncate?: boolean;
  className?: string;
  testId?: string;
  tabIndex?: number;
  ariaHidden?: boolean;
  ariaLabel?: string;
};

export const ConditionValueBadge = forwardRef<HTMLDivElement, ConditionValueBadgeProps>(function ConditionValueBadge(
  {
    value,
    tooltipLabel,
    tooltipOpenDelay = 0,
    onClick,
    truncate = false,
    className = '',
    testId,
    tabIndex,
    ariaHidden,
    ariaLabel,
  },
  ref,
) {
  const badge = (
    <Badge
      ref={ref}
      className={`${classes.badge} ${truncate ? classes.badgeTruncated : ''} ${onClick ? classes.interactive : ''} ${className}`}
      variant="outline"
      onClick={onClick}
      tabIndex={tabIndex}
      aria-hidden={ariaHidden || undefined}
      aria-label={ariaLabel}
      data-testid={testId}
    >
      <TextWithEmoji text={value} className={truncate ? classes.badgeLabelTruncated : undefined} />
    </Badge>
  );

  return tooltipLabel ? (
    <Tooltip label={tooltipLabel} openDelay={tooltipOpenDelay}>
      {badge}
    </Tooltip>
  ) : (
    badge
  );
});

type ConditionValueOperatorProps = {
  operator: SurveyLogicOperator;
  label: string;
  onClick?: () => void;
  testId?: string;
};

export function ConditionValueOperator({ operator, label, onClick, testId }: ConditionValueOperatorProps) {
  return (
    <Tooltip label={label} openDelay={300}>
      <Box
        className={`${classes.operator} ${onClick ? classes.interactive : ''}`}
        onClick={onClick}
        aria-label={label}
        data-testid={testId}
      >
        <FontAwesomeIcon
          icon={operator === 'and' ? faArrowsSplitUpAndLeft : faArrowsCross}
          className={`${classes.operatorIcon} ${operator === 'or' ? classes.operatorIconOr : ''}`}
        />
      </Box>
    </Tooltip>
  );
}

type Props = {
  value: string;
  valueTooltipLabel?: string;
  valueTooltipOpenDelay?: number;
  onValueClick?: () => void;
  truncateValue?: boolean;
  valueTestId?: string;
  operator?: SurveyLogicOperator;
  operatorLabel?: string;
  onOperatorClick?: () => void;
  operatorTestId?: string;
};

export function ConditionValueToken({
  value,
  valueTooltipLabel,
  valueTooltipOpenDelay,
  onValueClick,
  truncateValue,
  valueTestId,
  operator,
  operatorLabel,
  onOperatorClick,
  operatorTestId,
}: Props) {
  return (
    <Box className={classes.token}>
      {operator && operatorLabel && (
        <ConditionValueOperator
          operator={operator}
          label={operatorLabel}
          onClick={onOperatorClick}
          testId={operatorTestId}
        />
      )}

      <ConditionValueBadge
        value={value}
        tooltipLabel={valueTooltipLabel}
        tooltipOpenDelay={valueTooltipOpenDelay}
        onClick={onValueClick}
        truncate={truncateValue}
        testId={valueTestId}
      />
    </Box>
  );
}
